/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { floorPlanIconSvg, type FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { useLengthFormatter } from '@kaiizen/kai-floor-plan-editor';
import {
  getAllFlooringAreaValueNames,
  getGroupMeasurements,
  getGroupMeasurementTotals,
  getGroupsInStandardGroupOrder,
  groupHasRoomAreaTemplate,
  ScanGroupOptimized,
  ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';
import { Fragment, useMemo } from 'react';

import { formatWholeMeasurement, getFlooringAreaTotals, getGroupDimensions, getGroupFlooringArea } from '@/Client/Lib/roomGroupMeasurementsLib';
import { flooringAreaValueNamePresenter, groupNamePresenter } from '@/Client/Presenters';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  ModalCard,
  ModalCloseButton,
  ModalDivider,
  ModalShell,
  ModalTitle,
} from '@/Client/Widgets';
import { MODAL_COLORS } from '@/Client/Widgets/Modal/ModalStyleLib';

const OVERVIEW_GRID_STYLE = css`
  label: OVERVIEW_GRID_STYLE;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px 20px;
  margin-top: 20px;
`;

const SECTION_LABEL_STYLE = css`
  label: SECTION_LABEL_STYLE;
  ${TYPOGRAPHY2.medium}
  color: ${MODAL_COLORS.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OVERVIEW_STAT_STYLE = css`
  label: OVERVIEW_STAT_STYLE;
  font-family: CircularXX, sans-serif;
  font-size: ${pxToRem(22)};
  font-weight: 700;
  line-height: 1.273;
  color: ${MODAL_COLORS.text};
`;

const OVERVIEW_CAPTION_STYLE = css`
  label: OVERVIEW_CAPTION_STYLE;
  ${TYPOGRAPHY2.medium}
  color: ${MODAL_COLORS.textMuted};
  margin-bottom: 4px;
`;

const MODAL_LAYOUT_STYLE = css`
  label: MODAL_LAYOUT_STYLE;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 90px);
  overflow: hidden;
`;

const MODAL_TOP_SECTION_STYLE = css`
  label: MODAL_TOP_SECTION_STYLE;
  flex-shrink: 0;
`;

const TABLE_SCROLL_WRAP_STYLE = css`
  label: TABLE_SCROLL_WRAP_STYLE;
  flex: 1;
  min-height: 0;
  margin-top: 12px;
  overflow: auto;
  border: 1px solid ${MODAL_COLORS.divider};
  border-radius: 8px;
`;

const TABLE_STYLE = css`
  label: TABLE_STYLE;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  ${TYPOGRAPHY2.medium}
  color: ${MODAL_COLORS.text};
`;

const TABLE_HEAD_CELL_STYLE = css`
  label: TABLE_HEAD_CELL_STYLE;
  position: sticky;
  top: 0;
  z-index: 2;
  text-align: left;
  vertical-align: middle;
  padding: 12px 10px;
  background: ${COLORS2.namedColors.lightestGray};
  border-bottom: 1px solid ${MODAL_COLORS.divider};
`;

const TABLE_HEAD_LABEL_STYLE = css`
  label: TABLE_HEAD_LABEL_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: ${MODAL_COLORS.text};
`;

const TABLE_CELL_STYLE = css`
  label: TABLE_CELL_STYLE;
  padding: 10px;
  vertical-align: middle;
  border-bottom: 1px solid ${MODAL_COLORS.divider};
`;

const TABLE_EMPTY_CELL_STYLE = css`
  label: TABLE_EMPTY_CELL_STYLE;
  ${TYPOGRAPHY2.medium}
`;

const TABLE_CELL_NUM_STYLE = css`
  label: TABLE_CELL_NUM_STYLE;
  padding: 10px;
  vertical-align: middle;
  border-bottom: 1px solid ${MODAL_COLORS.divider};
  ${TYPOGRAPHY2.mediumBold}
  text-align: right;
`;

const TABLE_CELL_NUM_UNIT_STYLE = css`
  label: TABLE_CELL_NUM_UNIT_STYLE;
  ${TYPOGRAPHY2.small}
  color: ${MODAL_COLORS.textMuted};
  margin-left: 4px;
`;

const TABLE_CELL_NAME_STYLE = css`
  label: TABLE_CELL_NAME_STYLE;
`;

const GROUP_NAME_COLUMN_STYLE = css`
  label: GROUP_NAME_COLUMN_STYLE;
  min-width: 200px;
`;

const DIMENSIONS_COLUMN_STYLE = css`
  label: DIMENSIONS_COLUMN_STYLE;
  min-width: 150px;
`;

/** Fixed-width, right-aligned header for single-measurement columns (Area, Perimeter, Walls, flooring types). */
const NUMBER_COLUMN_STYLE = css`
  label: NUMBER_COLUMN_STYLE;
  text-align: right;
  width: 100px;
  min-width: 100px;
`;

const CATEGORY_ROW_STYLE = css`
  label: CATEGORY_ROW_STYLE;
  background: ${COLORS2.namedColors.offWhite};
  font-weight: 700;
`;

const GROUP_ROW_STYLE = css`
  label: GROUP_ROW_STYLE;
  background: ${MODAL_COLORS.white};
`;

const GROUP_ROW_INTERACTIVE_STYLE = css`
  label: GROUP_ROW_INTERACTIVE_STYLE;
  cursor: pointer;
  &:hover {
    background: ${COLORS2.namedColors.offWhite};
  }
`;

const NumberCell = ({ value, unit }: { value: number | undefined; unit: string }) => (
  (value == null) ? <td css={[TABLE_CELL_NUM_STYLE, TABLE_EMPTY_CELL_STYLE]}>—</td> : (
    <td css={TABLE_CELL_NUM_STYLE}>
      {formatWholeMeasurement(value)}
      {unit ? <span css={TABLE_CELL_NUM_UNIT_STYLE}>{unit}</span> : null}
    </td>
  )
);

const ROOM_OUTLINE_ICON_STYLE = css`
  label: ROOM_OUTLINE_ICON_STYLE;
  display: inline-flex;
  flex-shrink: 0;
  width: 32px;
  height: 24px;
  color: ${COLORS2.namedColors.charcoal};

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

/**
 * Iconic walls-only outline of the group's room, from the shared floor-plan
 * icon renderer — normalized to a fixed line thickness regardless of room
 * size. Groups with only legacy dimensions get a plain rectangle
 * (synthesized plan), so every measured row carries its shape.
 */
const RoomOutlineIcon = ({ room }: { room: FloorPlanRoom }) => (
  <span
    css={ROOM_OUTLINE_ICON_STYLE}
    dangerouslySetInnerHTML={{
      __html: floorPlanIconSvg(room, {
        lineThickness: 2,
        padding: 1,
      }),
    }}
  />
);

const DIMENSION_CELL_CONTENT_STYLE = css`
  label: DIMENSION_CELL_CONTENT_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const formatDimensionCell = (group: ScanGroupOptimized, formatLength: (lengthFeet: number) => string) => {
  const { width, length, height } = getGroupDimensions(group);
  if (width == null || length == null) return '—';
  const plan = group.roomFloorPlan;
  return (
    <div css={DIMENSION_CELL_CONTENT_STYLE}>
      <RoomOutlineIcon room={plan ?? { width, length, height }} />
      <span>
        {`${formatLength(width)} × ${formatLength(length)} × ${height == null ? '—' : formatLength(height)}`}
      </span>
    </div>
  );
};

export type ProjectMeasurementsModalProps = {
  listedSqft?: number | null;
  onClose: () => void;
  onSelectGroup?: (groupId: string) => void;
  scanRoot: ScanRootOptimized | undefined;
  visible: boolean;
};

export const ProjectMeasurementsModal = ({
  listedSqft,
  onClose,
  onSelectGroup,
  scanRoot,
  visible,
}: ProjectMeasurementsModalProps) => {
  const formatLength = useLengthFormatter();
  const { clusterOrder, groupClusters } = useMemo(() => {
    if (!scanRoot) {
      return {
        clusterOrder: [],
        groupClusters: {} as Record<string, ScanGroupOptimized[]>,
      };
    }
    const roomGroups = scanRoot.groups.filter(groupHasRoomAreaTemplate);
    return getGroupsInStandardGroupOrder(scanRoot.template, roomGroups);
  }, [scanRoot]);

  /** All flooring types defined across the template's groups, alphabetical by display label. */
  const flooringValueNames = useMemo(() => {
    if (!scanRoot) return [];
    return getAllFlooringAreaValueNames(scanRoot.template).sort((a, b) => flooringAreaValueNamePresenter(a).localeCompare(flooringAreaValueNamePresenter(b)));
  }, [scanRoot]);

  const overview = useMemo(() => {
    const roomGroups = scanRoot?.groups?.filter(groupHasRoomAreaTemplate) ?? [];
    const { area, perimeter, wallArea } = getGroupMeasurementTotals(roomGroups);
    return {
      measuredArea: area,
      perimeter,
      wallArea,
      flooringTotals: getFlooringAreaTotals(roomGroups, flooringValueNames),
    };
  }, [scanRoot, flooringValueNames]);

  return (
    <ModalShell visible={visible} onClose={onClose} contentMaxWidth='none'>
      <ModalCard>
        <ModalCloseButton onClick={onClose} />
        <div css={MODAL_LAYOUT_STYLE}>
          <div css={MODAL_TOP_SECTION_STYLE}>
            <ModalTitle>Project Measurements</ModalTitle>
            <ModalDivider />

            <div css={SECTION_LABEL_STYLE}>Overview</div>
            <div css={OVERVIEW_GRID_STYLE}>
              <div>
                <div css={OVERVIEW_CAPTION_STYLE}>Listed area (sqft)</div>
                <div css={OVERVIEW_STAT_STYLE}>
                  {listedSqft != null && listedSqft > 0
                    ? formatWholeMeasurement(listedSqft)
                    : '—'}
                </div>
              </div>
              <div>
                <div css={OVERVIEW_CAPTION_STYLE}>Measured area (sqft)</div>
                <div css={OVERVIEW_STAT_STYLE}>
                  {formatWholeMeasurement(overview.measuredArea)}
                </div>
              </div>
              <div>
                <div css={OVERVIEW_CAPTION_STYLE}>Perimeter (ft)</div>
                <div css={OVERVIEW_STAT_STYLE}>
                  {formatWholeMeasurement(overview.perimeter)}
                </div>
              </div>
              <div>
                <div css={OVERVIEW_CAPTION_STYLE}>Walls (sqft)</div>
                <div css={OVERVIEW_STAT_STYLE}>
                  {formatWholeMeasurement(overview.wallArea)}
                </div>
              </div>
              {flooringValueNames.map(valueName => (
                <div key={valueName}>
                  <div css={OVERVIEW_CAPTION_STYLE}>
                    {`${flooringAreaValueNamePresenter(valueName)} (sqft)`}
                  </div>
                  <div css={OVERVIEW_STAT_STYLE}>
                    {formatWholeMeasurement(overview.flooringTotals[valueName])}
                  </div>
                </div>
              ))}
            </div>

            <ModalDivider />

            <div css={SECTION_LABEL_STYLE}>
              <span>Groups measurements</span>
            </div>
          </div>

          <div css={TABLE_SCROLL_WRAP_STYLE}>
            <table css={TABLE_STYLE}>
              <thead>
                <tr>
                  <th css={[TABLE_HEAD_CELL_STYLE, GROUP_NAME_COLUMN_STYLE]}>
                    <span css={TABLE_HEAD_LABEL_STYLE}>Group Name</span>
                  </th>
                  <th css={[TABLE_HEAD_CELL_STYLE, DIMENSIONS_COLUMN_STYLE]}>
                    <span css={TABLE_HEAD_LABEL_STYLE}>Width × Length × Height</span>
                  </th>
                  <th css={[TABLE_HEAD_CELL_STYLE, NUMBER_COLUMN_STYLE]}>
                    <span css={TABLE_HEAD_LABEL_STYLE}>Area</span>
                  </th>
                  <th css={[TABLE_HEAD_CELL_STYLE, NUMBER_COLUMN_STYLE]}>
                    <span css={TABLE_HEAD_LABEL_STYLE}>Perimeter</span>
                  </th>
                  <th css={[TABLE_HEAD_CELL_STYLE, NUMBER_COLUMN_STYLE]}>
                    <span css={TABLE_HEAD_LABEL_STYLE}>Walls</span>
                  </th>
                  {flooringValueNames.map(valueName => (
                    <th key={valueName} css={[TABLE_HEAD_CELL_STYLE, NUMBER_COLUMN_STYLE]}>
                      <span css={TABLE_HEAD_LABEL_STYLE}>
                        {flooringAreaValueNamePresenter(valueName)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clusterOrder.map(category => {
                  const groupsInCategory = groupClusters[category] ?? [];
                  if (groupsInCategory.length === 0) return null;
                  const categoryTotals = getGroupMeasurementTotals(groupsInCategory);
                  const categoryFlooringTotals = getFlooringAreaTotals(groupsInCategory, flooringValueNames);
                  return (
                    <Fragment key={category}>
                      <tr css={CATEGORY_ROW_STYLE}>
                        <td colSpan={2} css={TABLE_CELL_STYLE}>
                          {`${category} Groups (totals)`}
                        </td>
                        <NumberCell value={categoryTotals.area} unit='sqft' />
                        <NumberCell value={categoryTotals.perimeter} unit='ft' />
                        <NumberCell value={categoryTotals.wallArea} unit='sqft' />
                        {flooringValueNames.map(valueName => <NumberCell key={valueName} value={categoryFlooringTotals[valueName]} unit='sqft' />)}
                      </tr>
                      {groupsInCategory.map(group => {
                        const values = getGroupMeasurements(group);
                        return (
                          <tr
                            key={group.id}
                            css={[
                              GROUP_ROW_STYLE,
                              onSelectGroup && GROUP_ROW_INTERACTIVE_STYLE,
                            ]}
                            onClick={onSelectGroup
                              ? () => onSelectGroup(group.id)
                              : undefined}
                          >
                            <td css={[TABLE_CELL_STYLE, TABLE_CELL_NAME_STYLE]}>
                              {groupNamePresenter(group)}
                            </td>
                            <td css={TABLE_CELL_STYLE}>
                              {formatDimensionCell(group, formatLength)}
                            </td>
                            <NumberCell value={values.area} unit='sqft' />
                            <NumberCell value={values.perimeter} unit='ft' />
                            <NumberCell value={values.wallArea} unit='sqft' />
                            {flooringValueNames.map(valueName => (
                              <NumberCell
                                key={valueName}
                                value={getGroupFlooringArea(group, valueName)}
                                unit='sqft'
                              />
                            ))}
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ModalCard>
    </ModalShell>
  );
};
